import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CalendarDays, Loader2, RefreshCw, FileDown, FileText, Clock, Edit, Edit3,
  ExternalLink, Trophy, GripVertical, CheckCircle, UserPlus, Trash2
} from "lucide-react";
import { exportSchedulePDF, exportFITPSchedulePDF } from "@/lib/pdfExport";
import { buildPdfVenue } from "@/lib/pdfVenue";
import { computeGlobalLabelOverrides } from "@/lib/bracketUtils";

const ROUND_LABELS = {
  R256: "Turno Prel.", R128: "128-esimi", R64: "64-esimi", R32: "32-esimi",
  R16: "Sedicesimi", QF: "Quarti", SF: "Semifinale", F: "Finale",
};

// Looks up per-day court name, falls back to any date, then "Campo N"
const getCourtName = (courtNames, courtNum, dateStr) => {
  if (!courtNum) return "";
  const num = String(courtNum);
  if (courtNames && dateStr && courtNames[dateStr]) {
    const name = courtNames[dateStr][num] || courtNames[dateStr][courtNum];
    if (name && name.trim()) return name.trim();
  }
  if (courtNames) {
    for (const dayNames of Object.values(courtNames)) {
      if (typeof dayNames === "object" && dayNames) {
        const name = dayNames[num] || dayNames[courtNum];
        if (name && name.trim()) return name.trim();
      }
    }
  }
  return `Campo ${courtNum}`;
};

const SchedulePanel = ({ tournament, venue }) => {
  const [schedule, setSchedule] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const [filterCourt, setFilterCourt] = useState("all");
  const [filterDate, setFilterDate] = useState("all");
  const [editMatch, setEditMatch] = useState(null);
  const [dialogMode, setDialogMode] = useState("reschedule");
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [rescheduleCourt, setRescheduleCourt] = useState("");
  const [rescheduleStartTime, setRescheduleStartTime] = useState("");
  const [rescheduleEndTime, setRescheduleEndTime] = useState("");
  const [rescheduling, setRescheduling] = useState(false);
  const [unscheduledDetails, setUnscheduledDetails] = useState([]);
  const [winnerId, setWinnerId] = useState("");
  const [matchScore, setMatchScore] = useState("");
  const [statusPosition, setStatusPosition] = useState("player1");
  const [statusValue, setStatusValue] = useState("");
  const [catPlayers, setCatPlayers] = useState([]);
  const [dragMatch, setDragMatch] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [scheduleNotes, setScheduleNotes] = useState(tournament?.pdf_schedule_notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  // Clear-schedule double confirmation state: "first" → "second" → executes
  const [clearStep, setClearStep] = useState(null);
  const [clearing, setClearing] = useState(false);

  const clearSchedule = async () => {
    setClearing(true);
    try {
      const r = await api.delete(`/tournaments/${tournament.tournament_id}/schedule`);
      toast.success(`Calendario eliminato (${r.cleared || 0} partite)`);
      setClearStep(null);
      fetchSchedule();  // refresh the schedule list in this panel
      // Notify parent (e.g. BracketPanel mounted as sibling) so it can re-fetch matches too.
      if (typeof onScheduleChange === "function") onScheduleChange();
    } catch (e) {
      toast.error(e.message || "Errore nell'eliminazione del calendario");
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    setScheduleNotes(tournament?.pdf_schedule_notes || "");
  }, [tournament?.pdf_schedule_notes]);

  const saveScheduleNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/tournaments/${tournament.tournament_id}`, { pdf_schedule_notes: scheduleNotes });
      tournament.pdf_schedule_notes = scheduleNotes; // optimistic local sync
      toast.success("Note salvate");
      setShowNotesDialog(false);
    } catch (e) {
      toast.error("Errore nel salvataggio delle note");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleDragStart = (e, match) => {
    setDragMatch(match);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", match.match_id);
    e.currentTarget.style.opacity = "0.5";
  };
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    setDragMatch(null);
    setDragOverDate(null);
  };
  const handleDragOver = (e, date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverDate(date);
  };
  const handleDragLeave = () => { setDragOverDate(null); };
  const handleDrop = async (e, targetDate) => {
    e.preventDefault();
    setDragOverDate(null);
    if (!dragMatch) return;
    const oldDate = dragMatch.scheduled_date;
    if (oldDate === targetDate) { setDragMatch(null); return; }
    try {
      await api.put(`/tournaments/${tournament.tournament_id}/matches/${dragMatch.match_id}/reschedule`, {
        scheduled_date: targetDate,
        scheduled_time: dragMatch.scheduled_time,
        scheduled_court: dragMatch.scheduled_court,
      });
      toast.success(`Partita spostata al ${targetDate}`);
      fetchSchedule();
    } catch (err) { toast.error(err.message || "Errore nello spostamento"); }
    setDragMatch(null);
  };

  const fetchSchedule = useCallback(async () => {
    try { setSchedule(await api.get(`/tournaments/${tournament.tournament_id}/schedule`)); } catch (e) { setSchedule(null); console.error("Failed to load schedule:", e); }
  }, [tournament.tournament_id]);
  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

  // Fetch full draw matches (including BYEs) for correct global label calculation
  const [drawMatches, setDrawMatches] = useState([]);
  useEffect(() => {
    const cats = tournament.categories || [];
    if (cats.length === 0) return;
    Promise.all(cats.map(c =>
      api.get(`/tournaments/${tournament.tournament_id}/categories/${c.category_id}/draw`).then(d => d.matches || []).catch(() => [])
    )).then(results => setDrawMatches(results.flat()));
  }, [tournament.tournament_id, tournament.categories]);
  const generateSchedule = async () => {
    setGenerating(true);
    try { const r = await api.post(`/tournaments/${tournament.tournament_id}/schedule/generate`); toast.success(`${r.scheduled} partite pianificate${r.unscheduled > 0 ? `, ${r.unscheduled} non pianificate` : ""}`); setUnscheduledDetails(r.unscheduled_details || []); fetchSchedule(); } catch (e) { toast.error("Errore"); } finally { setGenerating(false); }
  };

  const openReschedule = (m, mode) => {
    setEditMatch(m);
    setRescheduleDate(m.scheduled_date || "");
    setRescheduleTime(m.scheduled_time || "");
    setRescheduleCourt(m.scheduled_court != null ? String(m.scheduled_court) : "");
    setRescheduleStartTime(m.start_time || "");
    setRescheduleEndTime(m.end_time || "");
    setWinnerId(m.winner_id || "");
    setMatchScore(m.score || "");
    setStatusPosition("player1"); setStatusValue("");
    if (mode) {
      setDialogMode(mode);
    } else {
      const p1Real = m.player1_id && m.player1_id !== "BYE";
      const p2Real = m.player2_id && m.player2_id !== "BYE";
      setDialogMode(p1Real && p2Real ? "result" : "reschedule");
    }
    // Fetch players for the match's category
    if (m.category_id) {
      api.get(`/tournaments/${tournament.tournament_id}/categories/${m.category_id}/players`)
        .then(setCatPlayers).catch(() => setCatPlayers([]));
    } else {
      setCatPlayers([]);
    }
  };

  const submitResult = async () => {
    if (!winnerId) { toast.error("Seleziona il vincitore"); return; }
    try {
      await api.put(`/tournaments/${tournament.tournament_id}/matches/${editMatch.match_id}`, { winner_id: winnerId, score: matchScore });
      if (rescheduleStartTime || rescheduleEndTime) {
        const timePayload = {};
        if (rescheduleStartTime) timePayload.start_time = rescheduleStartTime;
        if (rescheduleEndTime) timePayload.end_time = rescheduleEndTime;
        await api.put(`/tournaments/${tournament.tournament_id}/matches/${editMatch.match_id}/reschedule`, timePayload);
      }
      toast.success("Risultato registrato!");
      setEditMatch(null); fetchSchedule();
    } catch (e) { toast.error("Errore"); }
  };

  const submitStatus = async () => {
    if (!statusValue) { toast.error("Seleziona un giocatore"); return; }
    try {
      await api.put(`/tournaments/${tournament.tournament_id}/matches/${editMatch.match_id}/swap`, {
        match_id: editMatch.match_id, position: statusPosition, new_player_id: statusValue,
      });
      toast.success(statusValue === "BYE" ? "BYE assegnato!" : statusValue === "TBD" ? "Impostato come TBD" : "Giocatore assegnato!");
      setEditMatch(null); fetchSchedule();
    } catch (e) { toast.error("Errore"); }
  };

  const submitReschedule = async () => {
    if (!editMatch) return;
    setRescheduling(true);
    try {
      const payload = {};
      if (rescheduleDate) payload.scheduled_date = rescheduleDate;
      if (rescheduleTime) payload.scheduled_time = rescheduleTime;
      if (rescheduleCourt) payload.scheduled_court = parseInt(rescheduleCourt, 10);
      if (rescheduleStartTime) payload.start_time = rescheduleStartTime;
      if (rescheduleEndTime) payload.end_time = rescheduleEndTime;
      await api.put(`/tournaments/${tournament.tournament_id}/matches/${editMatch.match_id}/reschedule`, payload);
      toast.success("Partita riprogrammata!");
      setEditMatch(null);
      fetchSchedule();
    } catch (e) { toast.error(e.message || "Errore"); } finally { setRescheduling(false); }
  };

  // Single-match deletion: clear scheduled_date/time/court/slot for ONE match only.
  // Asks for confirmation before performing the action.
  const [confirmSingleClear, setConfirmSingleClear] = useState(false);
  const clearSingleMatch = async () => {
    if (!editMatch) return;
    setRescheduling(true);
    try {
      await api.delete(`/tournaments/${tournament.tournament_id}/matches/${editMatch.match_id}/schedule`);
      toast.success("Programmazione partita eliminata");
      setConfirmSingleClear(false);
      setEditMatch(null);
      fetchSchedule();
    } catch (e) { toast.error(e.message || "Errore eliminazione partita"); }
    finally { setRescheduling(false); }
  };

  const categories = tournament.categories || [];
  const allMatches = useMemo(() => schedule?.matches || [], [schedule]);

  // Compute global label overrides per category using FULL draw (including BYEs)
  const labelOverrides = useMemo(() => {
    const source = drawMatches.length > 0 ? drawMatches : allMatches;
    const byCat = {};
    for (const m of source) {
      const cid = m.category_id || "_";
      if (!byCat[cid]) byCat[cid] = [];
      byCat[cid].push(m);
    }
    let result = {};
    for (const catMatches of Object.values(byCat)) {
      const overrides = computeGlobalLabelOverrides(catMatches);
      result = { ...result, ...overrides };
    }
    return result;
  }, [drawMatches, allMatches]);
  const filteredMatches = useMemo(
    () => {
      let matches = filterCat === "all" ? allMatches : allMatches.filter(m => m.category_id === filterCat);
      // Filter out BYE matches — they don't need to be played
      matches = matches.filter(m => m.status !== "bye" && m.player1_id !== "BYE" && m.player2_id !== "BYE");
      if (filterCourt !== "all") {
        matches = matches.filter(m => String(m.scheduled_court) === filterCourt);
      }
      if (filterDate !== "all") {
        matches = matches.filter(m => m.scheduled_date === filterDate);
      }
      return matches;
    },
    [allMatches, filterCat, filterCourt, filterDate]
  );
  const filteredByDate = useMemo(() => {
    const result = {};
    for (const m of filteredMatches) {
      const d = m.scheduled_date || "unscheduled";
      if (!result[d]) result[d] = [];
      result[d].push(m);
    }
    return result;
  }, [filteredMatches]);

  const availableCourts = useMemo(() => {
    const courts = new Set();
    for (const m of allMatches) {
      if (m.scheduled_court != null) courts.add(String(m.scheduled_court));
    }
    return [...courts].sort((a, b) => Number(a) - Number(b));
  }, [allMatches]);

  // Sorted list of unique scheduled dates (ISO YYYY-MM-DD).
  const availableDates = useMemo(() => {
    const dates = new Set();
    for (const m of allMatches) {
      if (m.scheduled_date) dates.add(m.scheduled_date);
    }
    return [...dates].sort();
  }, [allMatches]);

  // Build a "filtered schedule" object that mirrors the API response shape
  // but contains ONLY the matches that match the current filters (category,
  // court, date). The PDF exporters consume this so the user gets a PDF that
  // reflects the on-screen selection (e.g. just one day).
  // IMPORTANT: rebuild `by_date` too — the PDF export iterates over `by_date`,
  // not `matches`, so we must filter both shape projections.
  const filteredSchedule = useMemo(() => {
    if (!schedule) return null;
    const byDate = {};
    for (const m of filteredMatches) {
      const key = m.scheduled_date || "unscheduled";
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push(m);
    }
    return { ...schedule, matches: filteredMatches, by_date: byDate };
  }, [schedule, filteredMatches]);

  const formatDateLabel = (iso) => {
    if (!iso) return "";
    const parts = iso.split("-");
    if (parts.length !== 3) return iso;
    const [y, m, d] = parts;
    const dateObj = new Date(`${iso}T00:00:00`);
    const giorno = new Intl.DateTimeFormat("it-IT", { weekday: "short" }).format(dateObj);
    return `${giorno.charAt(0).toUpperCase()}${giorno.slice(1)} ${d}/${m}/${y.slice(2)}`;
  };

  // Detect conflicts: same court + overlapping time on same date
  const conflictMatchIds = useMemo(() => {
    const ids = new Set();
    const matchDuration = tournament.match_duration_minutes || 60;
    const scheduled = allMatches.filter(m => m.scheduled_date && m.scheduled_time && m.scheduled_court != null && m.status !== "bye");
    const byDateCourt = {};
    for (const m of scheduled) {
      const key = `${m.scheduled_date}|${m.scheduled_court}`;
      if (!byDateCourt[key]) byDateCourt[key] = [];
      byDateCourt[key].push(m);
    }
    for (const matches of Object.values(byDateCourt)) {
      if (matches.length < 2) continue;
      const sorted = [...matches].sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time));
      for (let i = 0; i < sorted.length; i++) {
        for (let j = i + 1; j < sorted.length; j++) {
          const [h1, m1] = sorted[i].scheduled_time.split(":").map(Number);
          const [h2, m2] = sorted[j].scheduled_time.split(":").map(Number);
          const t1 = h1 * 60 + m1;
          const t2 = h2 * 60 + m2;
          if (t1 === t2 || (t2 - t1) < matchDuration) {
            ids.add(sorted[i].match_id);
            ids.add(sorted[j].match_id);
          }
        }
      }
    }
    return ids;
  }, [allMatches, tournament.default_rest_time_minutes]);

  return (
    <Card className="bg-white">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-lg font-bold uppercase">Calendario</CardTitle>
        <div className="flex items-center gap-2">
          <Button data-testid="generate-schedule-btn" onClick={generateSchedule} disabled={generating} className="bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm gap-1" size="sm">
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />} Genera
          </Button>
          {allMatches.length > 0 && <Link to={`/tournament/${tournament.tournament_id}/timetable`}><Button variant="outline" size="sm" className="rounded-sm gap-1"><ExternalLink className="h-4 w-4" /> Vista Completa</Button></Link>}
          {allMatches.length > 0 && (
            <>
              <Button
                data-testid="export-schedule-pdf-btn"
                variant="outline"
                size="sm"
                className="rounded-sm gap-1 text-green-700 border-green-200 hover:bg-green-50"
                onClick={() => buildPdfVenue(venue).then((v) => exportSchedulePDF(tournament, filteredSchedule, v, drawMatches, tournament.pdf_schedule_notes || ""))}
              >
                <FileDown className="h-4 w-4" /> PDF
              </Button>
              <Button
                data-testid="export-fitp-pdf-btn"
                variant="outline"
                size="sm"
                className="rounded-sm gap-1 text-blue-700 border-blue-200 hover:bg-blue-50"
                onClick={() => buildPdfVenue(venue).then((v) => exportFITPSchedulePDF(tournament, filteredSchedule, v, drawMatches, tournament.pdf_schedule_notes || ""))}
              >
                <FileDown className="h-4 w-4" /> PDF con tutti i loghi
              </Button>
              <Button
                data-testid="edit-schedule-notes-btn"
                variant="outline"
                size="sm"
                className="rounded-sm gap-1"
                onClick={() => setShowNotesDialog(true)}
              >
                <FileText className="h-4 w-4" /> Note PDF
              </Button>
              <Button
                data-testid="clear-schedule-btn"
                onClick={() => setClearStep("first")}
                disabled={clearing}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase rounded-sm gap-1"
              >
                <Trash2 className="h-3.5 w-3.5" /> Elimina
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {allMatches.length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant="outline" className="rounded-sm">{filteredMatches.length} partite &middot; {Object.keys(filteredByDate).filter(d => d !== "unscheduled" && d !== "null").length} giorni</Badge>
              {categories.length > 1 && (
                <Select data-testid="schedule-category-filter" value={filterCat} onValueChange={setFilterCat}>
                  <SelectTrigger data-testid="schedule-category-filter-trigger" className="w-[200px] h-8 rounded-sm text-xs">
                    <SelectValue placeholder="Filtra categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le categorie</SelectItem>
                    {categories.map(c => (
                      <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {availableCourts.length > 1 && (
                <Select data-testid="schedule-court-filter" value={filterCourt} onValueChange={setFilterCourt}>
                  <SelectTrigger data-testid="schedule-court-filter-trigger" className="w-[160px] h-8 rounded-sm text-xs">
                    <SelectValue placeholder="Filtra campo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i campi</SelectItem>
                    {availableCourts.map(c => (
                      <SelectItem key={c} value={c}>{getCourtName(tournament?.court_names, c)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {availableDates.length > 1 && (
                <Select data-testid="schedule-date-filter" value={filterDate} onValueChange={setFilterDate}>
                  <SelectTrigger data-testid="schedule-date-filter-trigger" className="w-[200px] h-8 rounded-sm text-xs">
                    <SelectValue placeholder="Filtra giorno" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i giorni</SelectItem>
                    {availableDates.map(d => (
                      <SelectItem key={d} value={d}>{formatDateLabel(d)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="max-h-[500px] overflow-y-auto space-y-3 pr-1">
              {Object.entries(filteredByDate).filter(([d]) => d && d !== "unscheduled" && d !== "null").sort().map(([date, matches]) => (
                <div key={date}
                  className={`border rounded-sm p-3 transition-colors ${dragOverDate === date ? "border-green-500 bg-green-50/50 border-2" : "border-slate-200"}`}
                  onDragOver={(e) => handleDragOver(e, date)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, date)}
                >
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                    {date}
                    {dragOverDate === date && dragMatch && <span className="ml-2 text-green-600 normal-case tracking-normal font-normal">Rilascia qui per spostare</span>}
                  </p>
                  {matches.sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || "")).map((m) => {
                    const isCompleted = m.status === "completed";
                    const isDraggable = !isCompleted && m.status !== "bye";
                    const isConflict = conflictMatchIds.has(m.match_id);
                    const p1Name = m.player1_id === "BYE" ? "BYE" : m.player1_name || m.player1_tbd_label || "TBD";
                    const p2Name = m.player2_id === "BYE" ? "BYE" : m.player2_name || m.player2_tbd_label || "TBD";
                    const p1Rank = m.player1_ranking && m.player1_id && m.player1_id !== "BYE" && !p1Name.trim().endsWith(m.player1_ranking) ? m.player1_ranking : null;
                    const p2Rank = m.player2_ranking && m.player2_id && m.player2_id !== "BYE" && !p2Name.trim().endsWith(m.player2_ranking) ? m.player2_ranking : null;
                    return (
                      <div key={m.match_id}
                        draggable={isDraggable}
                        onDragStart={isDraggable ? (e) => handleDragStart(e, m) : undefined}
                        onDragEnd={isDraggable ? handleDragEnd : undefined}
                        className={`flex items-center gap-3 text-sm py-1 ${isConflict ? "bg-red-50 border border-red-300 rounded-sm px-2 -mx-1" : ""} ${isCompleted ? "opacity-70 cursor-pointer hover:opacity-90" : m.status === "bye" ? "" : "cursor-grab hover:bg-green-50 rounded-sm px-1 -mx-1 active:cursor-grabbing"}`}
                        onClick={() => m.status !== "bye" && openReschedule(m, isCompleted ? "reschedule" : undefined)}
                      >
                        {isDraggable && <GripVertical className="h-3.5 w-3.5 text-slate-300 shrink-0 -mr-1" />}
                        {isConflict && <span className="text-[10px] font-bold text-red-600 bg-red-100 px-1.5 py-0.5 rounded-sm shrink-0" data-testid={`conflict-badge-${m.match_id}`}>CONFLITTO</span>}
                        <span className="tabular-nums text-slate-600 w-12">{m.scheduled_time}</span>
                        <Badge variant="outline" className="rounded-sm text-xs">{getCourtName(tournament?.court_names, m.scheduled_court, m.scheduled_date)}</Badge>
                        {m.match_label && <Badge className="rounded-sm text-[10px] bg-green-100 text-green-800 hover:bg-green-100 font-bold">{labelOverrides[m.match_id] || m.match_label}</Badge>}
                        <span className="font-medium">
                          <span className={m.winner_id === m.player1_id ? "text-green-700 font-bold" : ""}>{p1Name}{p1Rank && <span className="text-blue-600 font-semibold ml-0.5 text-xs">{p1Rank}</span>}</span>
                          {" vs "}
                          <span className={m.winner_id === m.player2_id ? "text-green-700 font-bold" : ""}>{p2Name}{p2Rank && <span className="text-blue-600 font-semibold ml-0.5 text-xs">{p2Rank}</span>}</span>
                        </span>
                        {isCompleted && m.score && <span className="text-xs text-green-700 font-medium">{m.score}</span>}
                        {isCompleted && m.duration_minutes && (
                          <span className="text-[10px] text-slate-500 font-medium" data-testid={`schedule-duration-${m.match_id}`}>
                            {m.duration_minutes}′
                          </span>
                        )}
                        {isCompleted && <CheckCircle className="h-3.5 w-3.5 text-green-600 shrink-0" />}
                        {!isCompleted && m.status !== "bye" && (
                          <button data-testid={`schedule-edit-${m.match_id}`} onClick={(e) => { e.stopPropagation(); openReschedule(m); }} className="shrink-0 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-sm px-2 py-0.5 text-xs font-bold flex items-center gap-1 transition-colors" title="Modifica">
                            <Edit3 className="h-3 w-3" /> Modifica
                          </button>
                        )}
                        <Badge className="rounded-sm text-xs bg-slate-100 text-slate-600 hover:bg-slate-100 ml-auto shrink-0">{m.category_name || ROUND_LABELS[m.round] || m.round}</Badge>
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Unscheduled matches */}
              {(() => {
                const unsched = filteredMatches.filter(m => m.unscheduled_reason && !m.scheduled_date);
                if (unsched.length === 0 && unscheduledDetails.length === 0) return null;
                const items = unsched.length > 0 ? unsched : unscheduledDetails;
                return (
                  <div className="border-2 border-red-200 bg-red-50/50 rounded-sm p-4" data-testid="unscheduled-matches-panel">
                    <p className="text-sm font-bold uppercase tracking-wide text-red-700 mb-2">Partite non programmate ({items.length})</p>
                    <div className="space-y-2">
                      {items.map((m, idx) => (
                        <div key={m.match_id || idx} className="bg-white border border-red-200 rounded-sm p-2.5 cursor-pointer hover:bg-red-50 transition-colors" onClick={() => m.match_id && openReschedule(m, "reschedule")}>
                          <div className="flex items-center gap-2">
                            <Badge className="rounded-sm text-[10px] bg-red-100 text-red-700 hover:bg-red-100 font-bold shrink-0">
                              {m.category_name || m.category || ""}
                            </Badge>
                            <Badge variant="outline" className="rounded-sm text-[10px] shrink-0">
                              {ROUND_LABELS[m.round] || m.round || ""}
                            </Badge>
                            <span className="text-sm font-medium">
                              {m.player1_name || m.player1 || "TBD"}
                              {m.player1_ranking && !(m.player1_name || "").trim().endsWith(m.player1_ranking) && <span className="text-blue-600 font-semibold ml-0.5 text-xs">{m.player1_ranking}</span>}
                              {" vs "}
                              {m.player2_name || m.player2 || "TBD"}
                              {m.player2_ranking && !(m.player2_name || "").trim().endsWith(m.player2_ranking) && <span className="text-blue-600 font-semibold ml-0.5 text-xs">{m.player2_ranking}</span>}
                            </span>
                            {m.match_id && (
                              <button data-testid={`unscheduled-edit-${m.match_id || idx}`} className="ml-auto shrink-0 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-sm px-2 py-1 text-xs font-bold flex items-center gap-1 transition-colors" onClick={(e) => { e.stopPropagation(); openReschedule(m, "reschedule"); }}>
                                <Edit3 className="h-3 w-3" /> Modifica
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-red-600 mt-1 pl-1">
                            {m.unscheduled_reason || m.reason}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        ) : <div className="text-center py-12"><CalendarDays className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">Genera prima il tabellone, poi il calendario</p></div>}

        {/* Match Dialog - Same as Tabellone */}
        <Dialog open={!!editMatch} onOpenChange={() => setEditMatch(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold uppercase">
                {dialogMode === "result" ? "Inserisci Risultato" : dialogMode === "reschedule" ? "Riprogramma Incontro" : "Modifica Giocatore"}
              </DialogTitle>
            </DialogHeader>
            {editMatch && (
              <div>
                <Tabs value={dialogMode} onValueChange={setDialogMode}>
                  <TabsList className="w-full mb-4">
                    <TabsTrigger value="reschedule" className="flex-1 rounded-sm gap-1 text-xs">
                      <Clock className="h-3 w-3" /> Programmazione
                    </TabsTrigger>
                    <TabsTrigger value="result" className="flex-1 rounded-sm gap-1 text-xs" disabled={
                      !editMatch.player1_id || !editMatch.player2_id ||
                      editMatch.player1_id === "BYE" || editMatch.player2_id === "BYE"
                    }>
                      <Trophy className="h-3 w-3" /> Risultato
                    </TabsTrigger>
                    <TabsTrigger value="status" className="flex-1 rounded-sm gap-1 text-xs">
                      <UserPlus className="h-3 w-3" /> Modifica
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="result">
                    <div className="space-y-4">
                      <div className="bg-slate-50 rounded-sm p-4 text-center">
                        <p className="text-sm font-medium">
                          {editMatch.player1_id === "BYE" ? "BYE" : editMatch.player1_name || editMatch.player1_tbd_label || "TBD"}
                          <span className="text-slate-400 mx-2">vs</span>
                          {editMatch.player2_id === "BYE" ? "BYE" : editMatch.player2_name || editMatch.player2_tbd_label || "TBD"}
                        </p>
                        <div className="flex items-center justify-center gap-2 mt-1">
                          <Badge className="rounded-sm text-xs bg-slate-100 text-slate-700 hover:bg-slate-100">{ROUND_LABELS[editMatch.round] || editMatch.round}</Badge>
                          <span className="text-xs text-slate-400">{editMatch.category_name}</span>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setDialogMode("status")} className="mt-2 text-xs rounded-sm gap-1 border-slate-300 hover:border-green-500 hover:bg-green-50">
                          <Edit className="h-3 w-3" /> Modifica
                        </Button>
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase">Vincitore</Label>
                        <Select value={winnerId} onValueChange={setWinnerId}>
                          <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Seleziona vincitore" /></SelectTrigger>
                          <SelectContent>
                            {editMatch.player1_id && editMatch.player1_id !== "BYE" && <SelectItem value={editMatch.player1_id}>{editMatch.player1_name}</SelectItem>}
                            {editMatch.player2_id && editMatch.player2_id !== "BYE" && <SelectItem value={editMatch.player2_id}>{editMatch.player2_name}</SelectItem>}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase">Punteggio</Label>
                        <Input value={matchScore} onChange={(e) => setMatchScore(e.target.value)} placeholder="es. 6-4 6-2" className="mt-1 rounded-sm" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-bold uppercase">Ora Inizio <span className="text-slate-400 font-normal normal-case">(opzionale)</span></Label>
                          <Input type="time" value={rescheduleStartTime} onChange={(e) => setRescheduleStartTime(e.target.value)} className="mt-1 rounded-sm" />
                        </div>
                        <div>
                          <Label className="text-xs font-bold uppercase">Ora Fine <span className="text-slate-400 font-normal normal-case">(opzionale)</span></Label>
                          <Input type="time" value={rescheduleEndTime} onChange={(e) => setRescheduleEndTime(e.target.value)} className="mt-1 rounded-sm" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={submitResult} className="flex-1 bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm">Salva Risultato</Button>
                        <Button variant="outline" onClick={() => setDialogMode("reschedule")} className="border-amber-500 text-amber-600 hover:bg-amber-50 font-bold uppercase rounded-sm text-xs">
                          <Clock className="h-3.5 w-3.5 mr-1" /> Riprogramma
                        </Button>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="reschedule">
                    <div className="space-y-3">
                      <div className="bg-slate-50 rounded-sm p-3 text-center text-sm">
                        <p className="font-medium">
                          {editMatch.player1_id === "BYE" ? "BYE" : editMatch.player1_name || editMatch.player1_tbd_label || "TBD"}
                          <span className="text-slate-400 mx-2">vs</span>
                          {editMatch.player2_id === "BYE" ? "BYE" : editMatch.player2_name || editMatch.player2_tbd_label || "TBD"}
                        </p>
                        <div className="flex items-center justify-center gap-2 mt-1">
                          <Badge className="rounded-sm text-xs bg-slate-100 text-slate-700 hover:bg-slate-100">{ROUND_LABELS[editMatch.round] || editMatch.round}</Badge>
                          <span className="text-xs text-slate-400">{editMatch.category_name}</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-bold uppercase">Data</Label>
                          <Input data-testid="schedule-reschedule-date" type="date" value={rescheduleDate} onChange={(e) => setRescheduleDate(e.target.value)} className="mt-1 rounded-sm" />
                        </div>
                        <div>
                          <Label className="text-xs font-bold uppercase">Campo</Label>
                          <Input data-testid="schedule-reschedule-court" type="number" min="1" value={rescheduleCourt} onChange={(e) => setRescheduleCourt(e.target.value)} className="mt-1 rounded-sm" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase">Orario Programmato</Label>
                        <Input data-testid="schedule-reschedule-time" type="time" value={rescheduleTime} onChange={(e) => setRescheduleTime(e.target.value)} className="mt-1 rounded-sm" />
                      </div>
                      <Button data-testid="schedule-reschedule-submit" onClick={submitReschedule} disabled={rescheduling} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase rounded-sm">
                        {rescheduling ? "Salvando..." : "Salva Programmazione"}
                      </Button>
                      {editMatch?.scheduled_date && (
                        <Button
                          data-testid="schedule-delete-single-btn"
                          variant="outline"
                          onClick={() => setConfirmSingleClear(true)}
                          disabled={rescheduling}
                          className="w-full mt-2 border-red-500 text-red-700 hover:bg-red-50 font-bold uppercase rounded-sm gap-1"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> Elimina questa partita dal calendario
                        </Button>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="status">
                    <div className="space-y-4">
                      <div className="bg-slate-50 rounded-sm p-4 text-center text-sm">
                        <p>Partita: <strong>{editMatch.player1_id === "BYE" ? "BYE" : editMatch.player1_name || editMatch.player1_tbd_label || "TBD"}</strong> vs <strong>{editMatch.player2_id === "BYE" ? "BYE" : editMatch.player2_name || editMatch.player2_tbd_label || "TBD"}</strong></p>
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase">Posizione</Label>
                        <Select value={statusPosition} onValueChange={setStatusPosition}>
                          <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="player1">{editMatch.player1_id === "BYE" ? "BYE" : editMatch.player1_name || editMatch.player1_tbd_label || "TBD"}</SelectItem>
                            <SelectItem value="player2">{editMatch.player2_id === "BYE" ? "BYE" : editMatch.player2_name || editMatch.player2_tbd_label || "TBD"}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-bold uppercase">Nuovo Giocatore</Label>
                        <Select value={statusValue} onValueChange={setStatusValue}>
                          <SelectTrigger data-testid="schedule-status-value" className="mt-1 rounded-sm"><SelectValue placeholder="Seleziona giocatore..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="BYE" className="text-slate-400 italic">BYE (salta turno)</SelectItem>
                            <SelectItem value="TBD" className="text-amber-500">TBD (da definire)</SelectItem>
                            {catPlayers.map(p => <SelectItem key={p.player_id} value={p.player_id}>{p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={submitStatus} className="w-full bg-green-700 hover:bg-green-800 text-white font-bold uppercase rounded-sm">Assegna Giocatore</Button>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </DialogContent>
        </Dialog>
        <AlertDialog open={confirmSingleClear} onOpenChange={setConfirmSingleClear}>
          <AlertDialogContent className="rounded-md">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading text-lg font-bold uppercase text-red-700">Eliminare questa partita?</AlertDialogTitle>
              <AlertDialogDescription>
                <span className="font-semibold text-red-700">
                  La programmazione (data, orario e campo) verra' rimossa SOLO per questa partita. Risultato e bracket NON verranno toccati.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-clear-single-match-btn"
                onClick={clearSingleMatch}
                disabled={rescheduling}
                className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
              >
                Si, elimina
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!clearStep} onOpenChange={(o) => !o && setClearStep(null)}>
          <AlertDialogContent className="rounded-md">
            <AlertDialogHeader>
              <AlertDialogTitle className={`font-heading text-lg font-bold uppercase ${clearStep === "second" ? "text-red-700" : ""}`}>
                {clearStep === "second" ? "Sei davvero sicuro?" : "Conferma Azione"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {clearStep === "first" && "Sei sicuro di voler eliminare il calendario? Tutte le programmazioni di tutte le categorie verranno rimosse (i risultati delle partite gia' giocate non verranno toccati)."}
                {clearStep === "second" && (
                  <span className="font-semibold text-red-700">
                    ATTENZIONE: questa azione cancellera' definitivamente la programmazione di TUTTE le partite del torneo. Procedo?
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-sm" onClick={() => setClearStep(null)}>Annulla</AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-clear-schedule-btn"
                onClick={() => {
                  if (clearStep === "first") setClearStep("second");
                  else clearSchedule();
                }}
                disabled={clearing}
                className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
              >
                {clearStep === "second" ? "Si, elimina definitivamente" : "Conferma"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
          <DialogContent className="rounded-sm max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg font-bold uppercase">Note sul PDF Calendario</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <p className="text-xs text-slate-500">Le note verranno stampate in fondo a ogni pagina del PDF del calendario (sia quello standard sia quello con tutti i loghi).</p>
              <Textarea
                data-testid="schedule-notes-textarea"
                value={scheduleNotes}
                onChange={(e) => setScheduleNotes(e.target.value)}
                placeholder="Es. ‘Inizio incontri: ore 9:00 — Per info: 06 1234567‘ . Max ~4 righe."
                rows={5}
                className="rounded-sm resize-none"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowNotesDialog(false)} className="rounded-sm">Annulla</Button>
              <Button data-testid="save-schedule-notes-btn" onClick={saveScheduleNotes} disabled={savingNotes} className="bg-green-700 hover:bg-green-800 text-white rounded-sm">
                {savingNotes ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Salva note
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};


export { SchedulePanel };
